import { getServerDate } from "./storage/date";

interface ILimiterOptions {
    resetAmountOnIntervalPass?: boolean;
    clearingInterval?: number;
    sizeLimit?: number;
}

interface IRecord<T> {
    intervalStart: Date;
    amount: number;
    dataSequence?: IData<T>[];
}

interface IData<T> {
    recordDate: Date;
    data: T;
}

/**
 * If you are storing data in this Limiter, .dataSequence will work as a RollingArray.
 * It means that Limiter will clean up all records from .dataSequence
 * - which are older than (currentDate - clearingInterval)
 * - which index is lower than (dataSequence.length - sizeLimit)
*/
export default class Limiter<T> {
    private readonly clearingInterval: number;
    private readonly sizeLimit?: number;
    private readonly records: { [hash: string]: IRecord<T> } = {};
    private readonly resetAmountOnIntervalPass: boolean;

    constructor(options: ILimiterOptions) {
        this.clearingInterval = options && options.clearingInterval ? options.clearingInterval : 60 * 60 * 1000;
        this.sizeLimit = options ? options.sizeLimit : undefined;
        this.resetAmountOnIntervalPass = options && options.resetAmountOnIntervalPass === false ? false : true;
    }

    didIntervalPass(limiterKey: string): boolean {
        let item = this.records[limiterKey];

        if (item) {
            let intervalStart = item.intervalStart.getTime();
            let currentDate = getServerDate().getTime();

            return intervalStart + this.clearingInterval <= currentDate;
        }

        return true;
    }

    /**
     * Adds record to the Limiter.
     * If clearing interval has passed, counter will be set to zero.
     * Data will be added according to the logic of RollingArray.
     */
    add(limiterKey: string, data?: T) {
        let item = this.records[limiterKey];

        if (!item) {
            this.records[limiterKey] = {
                intervalStart: getServerDate(),
                amount: 1
            };
        } else {
            if (!this.didIntervalPass(limiterKey)) {
                item.amount += 1;
            } else {
                if (this.resetAmountOnIntervalPass) {
                    item.amount = 1;
                } else {
                    item.amount += 1;
                }
            }
        }

        if (data) {
            this.addData(limiterKey, data);
        }
    }

    /**
     * Returns true if current amount of items is higher than defined limit.
     */
    isOverflowed(limiterKey: string): boolean {
        let item = this.records[limiterKey];

        if (item) {
            let didIntervalPass = this.didIntervalPass(limiterKey);
            let limitReached = item && this.sizeLimit && item.amount >= this.sizeLimit;

            return limitReached && !didIntervalPass ? true : false;
        } else {
            return false;
        }
    }

    /**
     * Returns current array of records.
     */
    getData(limiterKey: string): IData<T>[] {
        let item = this.records[limiterKey];

        if (item && item.dataSequence) {
            return item.dataSequence;
        }

        return [];
    }

    /**
     * Returns the last record.
     */
    getLast(limiterKey: string): IData<T> | null {
        let item = this.records[limiterKey];

        if (item && item.dataSequence) {
            return item.dataSequence[item.dataSequence.length - 1];
        }

        return null;
    }

    /*
     * Returns current amount of instances of record.
     */
    getAmount(recordKey) {
        let item = this.records[recordKey];
        return item ? item.amount : 0;
    }

    /*
     * Returns current amount of entities registered in limiter.
     */
    getEntitiesAmount() {
        return Object.keys(this.records).length;
    }

    /**
     * Returns amounts of all entiies as a HashMap.
     */
    getAmountHashMap(): { [recordKey: string]: number } {
        return Object.keys(this.records).reduce((acc, recordKey) => {
            let record = this.records[recordKey];
            if (record) {
                acc[recordKey] = record.amount;
            }
            return acc;
        }, {} as { [recordKey: string]: number });
    }

    /**
     * Completely clears up all records under passed key.
     */
    clear(limiterKey: string) {
        delete this.records[limiterKey];
    }

    private addData(limiterKey: string, data: T) {
        let item = this.records[limiterKey];
        let record = {
            recordDate: getServerDate(),
            data
        };

        if (item.dataSequence) {
            if (this.clearingInterval) {
                item.dataSequence = item.dataSequence.filter(dataFromSequence => {
                    let currentDate = getServerDate().getTime();
                    let recordDate = dataFromSequence.recordDate.getTime();

                    return currentDate < recordDate + this.clearingInterval;
                });
            }

            item.dataSequence.push(record);

            if (this.sizeLimit) {
                item.dataSequence = item.dataSequence.slice(-this.sizeLimit);
            }
        } else {
            item.dataSequence = [record];
        }
    }
}